/**
 * useLauncherAuthState.js — auth object the Apps menu passes to the catalog.
 * Shaped by: launcher prompt L1; decisions D-42-05, D-42-06 (owner 2026-10-12);
 *   G2a owner 2026-10-12 (FDE flag catalogs);
 *   G2b owner 2026-10-12 (specialtyKbReviewer claim);
 *   G2d owner 2026-10-12 (MorningReport learner profile, org-role Marketing Executive).
 * Invariants: read userRoleGrants with the owner uid query only; a failed read
 *   sets grantsReadFailed and does not add roles; signed-out callers do not query.
 *   FDE flags use the same hooks as DashboardPage. A failed flag read adds no tiles.
 *   specialtyKbReviewer is the JWT claim. A failed claim read adds no tile.
 *   hasMorningReportProfile uses useMsi3LearnerProfile. A failed listen is false.
 *   hasMorningReportFaculty uses useMsi3FacultyGrant, the same check as
 *   RequireMsi3Faculty (approved grant with orgId, or platform admin).
 *   A failed grant read stays false unless the caller is a platform admin.
 *   isOrgMarketingExecutive reads activeOrgRole only, not users.role.
 * Related: appCatalog.js launcherRolesForAuth, firebase/firestore.rules userRoleGrants.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import { useGlobalTciManager } from "hooks/useGlobalTciManager";
import { db } from "services/firebase";
import { useAsiLauncherAccess } from "features/asi/hooks/useAsiLauncherAccess";
import { useChartMindIAccess } from "features/chartMindI/hooks/useChartMindIAccess";
import { useChartMindI2Access } from "features/chartMindI2/hooks/useChartMindI2Access";
import { useVoxPontBetaAccess } from "features/voxpontBeta/useVoxPontBetaAccess";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import { useMsi3LearnerProfile } from "features/msi3Teaching/msi3LearnerProfile";
import { useMsi3FacultyGrant } from "features/msi3Teaching/msi3FacultyGrant";
import { isMarketingExecutiveUser } from "constants/userOrgRoles";

const NO_GRANTS = [];

/** JWT claim flags. A throw while reading claims is a failed read and stays false. */
function claimIsTrue(tokenClaims, name) {
  try {
    return tokenClaims?.[name] === true;
  } catch {
    return false;
  }
}

/**
 * Org membership only. Passing users.role would also match the dedicated
 * marketingExecutive account, which already has ROLE_APP_IDS.marketingExecutive.
 * A throw while reading the org role stays false.
 */
function readOrgMarketingExecutive(userData) {
  try {
    return isMarketingExecutiveUser({
      activeOrgRole: userData?.activeOrgRole,
    });
  } catch {
    return false;
  }
}

/**
 * Signup 42 writes uid, roleSlug, grantedBy, grantedAt, and source.
 * There is no status, active, revoked, or expiry field. A document in the
 * owner's snapshot is an active grant. Revocation removes the document.
 */
function activeGrantsFromSnapshot(snap, uid) {
  return (snap?.docs || [])
    .map((docSnap) => {
      const data = typeof docSnap.data === "function" ? docSnap.data() : {};
      return { id: docSnap.id, ...data };
    })
    .filter((grant) => grant.uid === uid && typeof grant.roleSlug === "string" && grant.roleSlug);
}

export function useLauncherAuthState() {
  const { user, userData, userLoading, isGlobalAdmin, isRegionalAdmin, tokenClaims } = useAuth();
  const { hasAsiAccess } = useAsiLauncherAccess();
  const { canAccess: hasChartMindIAccess } = useChartMindIAccess();
  const { canAccess: hasChartMindI2Access } = useChartMindI2Access();
  const { status: voxPontBetaStatus } = useVoxPontBetaAccess();
  // Same source as DashboardPage.jsx. useFdeRole clears assignments on a read
  // error, so those flags stay false. useGlobalTciManager falls back to the
  // JWT claim only; with no claim, a failed read stays false.
  const { isFdeDc, isFdeC, isFdeT, loading: fdeLoading } = useFdeRole();
  const { isGlobalTciManager, loading: gtmLoading } = useGlobalTciManager();
  const hasGtmAccess =
    Boolean(isGlobalTciManager) || Boolean(tokenClaims?.globalTciManager);
  const specialtyKbReviewer = claimIsTrue(tokenClaims, "specialtyKbReviewer");
  const {
    hasProfile: hasMorningReportProfile,
    loading: morningReportProfileLoading,
  } = useMsi3LearnerProfile();
  const { isFaculty: morningReportFaculty } = useMsi3FacultyGrant();
  const hasMorningReportFaculty = Boolean(morningReportFaculty);
  const isOrgMarketingExecutive = readOrgMarketingExecutive(userData);

  const uid = user?.uid || null;
  const [grantSnapshot, setGrantSnapshot] = useState({
    uid: null,
    roleGrants: NO_GRANTS,
    grantsReadFailed: false,
    resolved: false,
  });

  useEffect(() => {
    if (!uid) return undefined;

    let active = true;
    // Owner-read rule: resource.data.uid == request.auth.uid. The query
    // constraint has to be that equality or Firestore rejects the list.
    const unsubscribe = db.collection("userRoleGrants").where("uid", "==", uid).onSnapshot(
      (snap) => {
        if (!active) return;
        setGrantSnapshot({
          uid,
          roleGrants: activeGrantsFromSnapshot(snap, uid),
          grantsReadFailed: false,
          resolved: true,
        });
      },
      () => {
        if (!active) return;
        setGrantSnapshot({
          uid,
          roleGrants: NO_GRANTS,
          grantsReadFailed: true,
          resolved: true,
        });
      },
    );

    return () => {
      active = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [uid]);

  const grantsMatchUid = Boolean(uid) && grantSnapshot.uid === uid;
  const roleGrants = grantsMatchUid ? grantSnapshot.roleGrants : NO_GRANTS;
  const grantsReadFailed = grantsMatchUid ? grantSnapshot.grantsReadFailed : false;
  const grantsResolved = !uid || (grantsMatchUid && grantSnapshot.resolved);
  const userDataResolved = !uid || userLoading !== true;
  // Same bypass as VoxPont4Page: a dev account or the platform-admin JWT.
  // A failed voxpontBetaUsage read becomes status "none" (services/voxpontBeta.js).
  const voxPontDevBypass = isDevAuthFirestoreUser(userData) || Boolean(isGlobalAdmin);
  const hasVoxPontBeta = voxPontDevBypass || voxPontBetaStatus === "active";
  const voxPontBetaPending =
    Boolean(uid) && !voxPontDevBypass && voxPontBetaStatus === "loading";
  const flagsPending =
    Boolean(uid) && (fdeLoading || gtmLoading || morningReportProfileLoading);
  const loading =
    Boolean(uid) &&
    (!userDataResolved || !grantsResolved || voxPontBetaPending || flagsPending);

  const authState = useMemo(
    () => ({
      userData,
      isGlobalAdmin,
      isRegionalAdmin,
      hasAsiAccess,
      hasChartMindIAccess,
      hasChartMindI2Access,
      hasVoxPontBeta,
      isFdeDc: Boolean(isFdeDc),
      isFdeC: Boolean(isFdeC),
      isFdeT: Boolean(isFdeT),
      hasGtmAccess,
      specialtyKbReviewer,
      hasMorningReportProfile,
      hasMorningReportFaculty,
      isOrgMarketingExecutive,
      roleGrants,
      grantsReadFailed,
    }),
    [
      userData,
      isGlobalAdmin,
      isRegionalAdmin,
      hasAsiAccess,
      hasChartMindIAccess,
      hasChartMindI2Access,
      hasVoxPontBeta,
      isFdeDc,
      isFdeC,
      isFdeT,
      hasGtmAccess,
      specialtyKbReviewer,
      hasMorningReportProfile,
      hasMorningReportFaculty,
      isOrgMarketingExecutive,
      roleGrants,
      grantsReadFailed,
    ],
  );

  return { authState, loading };
}

export default useLauncherAuthState;
